import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Alert
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  ContentCopy,
  CheckCircle
} from '@mui/icons-material';

interface TemplateVariable {
  variable: string;
  description: string;
  example: string;
  available: string[];
}

const templateVariables: TemplateVariable[] = [
  {
    variable: '{marketName}',
    description: 'The name of the market',
    example: 'Tokyo',
    available: ['descriptions', 'key_findings', 'podcast']
  },
  {
    variable: '{country}',
    description: 'The country of the market',
    example: 'Japan',
    available: ['descriptions', 'key_findings', 'podcast']
  },
  {
    variable: '{monthYear}',
    description: 'Formatted month and year',
    example: 'March 2025',
    available: ['podcast']
  },
  {
    variable: '{reportMonth}',
    description: 'Report month in YYYYMM format',
    example: '202503',
    available: ['podcast']
  },
  {
    variable: '{pdfText}',
    description: 'Extracted text from the PDF (characters determined by PDF Characters setting)',
    example: 'The Tokyo hotel market demonstrated strong performance...',
    available: ['descriptions', 'key_findings', 'podcast']
  },
  {
    variable: '{year}',
    description: 'Year only',
    example: '2025',
    available: ['podcast']
  },
  {
    variable: '{month}',
    description: 'Month name',
    example: 'March',
    available: ['podcast']
  }
];

export const PromptVariablesDocs: React.FC = () => {
  const [expanded, setExpanded] = useState(true);
  const [copiedVar, setCopiedVar] = useState<string | null>(null);

  const handleCopyVariable = (variable: string) => {
    navigator.clipboard.writeText(variable);
    setCopiedVar(variable);
    setTimeout(() => setCopiedVar(null), 2000);
  };

  const getAvailabilityColor = (available: string[]): 'default' | 'primary' | 'secondary' | 'success' => {
    if (available.length === 3) return 'success';
    if (available.length === 2) return 'primary';
    if (available.length === 1) return 'secondary';
    return 'default';
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6">Template Variables</Typography>
        <IconButton size="small" onClick={() => setExpanded(!expanded)}>
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Use these variables in your prompts. They will be automatically replaced with actual values during processing.
          </Typography>
        </Alert>

        <Table size="small">
          <TableBody>
            {templateVariables.map((varInfo) => (
              <TableRow key={varInfo.variable} hover>
                <TableCell sx={{ width: 140 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{ 
                        fontFamily: 'monospace',
                        bgcolor: 'grey.100',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1
                      }}
                    >
                      {varInfo.variable}
                    </Typography>
                    <Tooltip title={copiedVar === varInfo.variable ? 'Copied!' : 'Copy'}>
                      <IconButton
                        size="small"
                        onClick={() => handleCopyVariable(varInfo.variable)}
                      >
                        {copiedVar === varInfo.variable ? (
                          <CheckCircle fontSize="small" color="success" />
                        ) : (
                          <ContentCopy fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {varInfo.description}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Example: {varInfo.example}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={`Available in: ${varInfo.available.join(', ')}`}>
                    <Chip
                      size="small"
                      label={varInfo.available.length}
                      color={getAvailabilityColor(varInfo.available)}
                    />
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Example Usage:
          </Typography>
          <Typography
            variant="body2"
            sx={{ 
              fontFamily: 'monospace',
              bgcolor: 'white',
              p: 1,
              borderRadius: 1,
              border: 1,
              borderColor: 'grey.300'
            }}
          >
            You are analyzing a MarketCompass hospitality report for {'{marketName}'} in {'{country}'}...
          </Typography>
        </Box>

        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Note:</strong> Not all variables are available for all prompt types. Check the availability indicator for each variable.
          </Typography>
        </Alert>
      </Collapse>
    </Paper>
  );
};